import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { IconPlus, IconTrash, IconChevronDown, IconChevronUp, IconTag } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const PriceTables = () => {
    const [appId, setAppId] = useState(null);
    const [tables, setTables] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [tableDetails, setTableDetails] = useState({});

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTableName, setNewTableName] = useState('');
    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');

    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [itemType, setItemType] = useState('produto');
    const [itemTableId, setItemTableId] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [itemValue, setItemValue] = useState('');

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarTabelas();
        (async () => {
            const [prodRes, servRes] = await Promise.all([
                api.get(`v1/apps/${appId}/products/`),
                api.get(`v1/apps/${appId}/services/`)
            ]);
            setProducts(prodRes.data || []);
            setServices(servRes.data.services || []);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const listarTabelas = async () => {
        const res = await api.get(`v1/apps/${appId}/price_tables/`);
        setTables(res.data.price_tables || []);
    };

    const toggleExpand = async (tableId) => {
        setExpanded((prev) => ({ ...prev, [tableId]: !prev[tableId] }));
        if (!tableDetails[tableId]) {
            const res = await api.get(`v1/apps/${appId}/price_tables/${tableId}/`);
            setTableDetails((prev) => ({ ...prev, [tableId]: res.data }));
        }
    };

    const refreshTable = async (tableId) => {
        const res = await api.get(`v1/apps/${appId}/price_tables/${tableId}/`);
        setTableDetails((prev) => ({ ...prev, [tableId]: res.data }));
    };

    const handleOrgSearchChange = async (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setSelectedOrg(null);
        if (!value) {
            setOrgResults([]);
            return;
        }
        const res = await api.get(`v1/apps/${appId}/organizations/?search=${encodeURIComponent(value)}`);
        setOrgResults(res.data.organizations || []);
    };

    const handleCreateTable = async () => {
        if (!newTableName.trim()) return;
        await api.post(`v1/apps/${appId}/price_tables/`, {
            name: newTableName,
            organization: selectedOrg?.id,
            valid_from: validFrom || undefined,
            valid_until: validUntil || undefined
        });
        setCreateDialogOpen(false);
        setNewTableName('');
        setSelectedOrg(null);
        setOrgSearch('');
        setValidFrom('');
        setValidUntil('');
        listarTabelas();
    };

    const handleDeleteTable = async (table) => {
        if (!window.confirm(`Excluir a tabela "${table.name}"?`)) return;
        await api.delete(`v1/apps/${appId}/price_tables/${table.id}/`);
        setTables((prev) => prev.filter((t) => t.id !== table.id));
    };

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];

    const handleAddItem = async () => {
        if (!itemTableId || !itemValue) return;
        const payload = { valor: itemValue };
        if (itemType === 'produto') {
            if (!selectedVariacaoId) return;
            payload.produto_variacao = selectedVariacaoId;
        } else {
            if (!selectedServiceId) return;
            payload.service = selectedServiceId;
        }
        await api.post(`v1/apps/${appId}/price_tables/${itemTableId}/items/`, payload);
        setSelectedProductId('');
        setSelectedVariacaoId('');
        setSelectedServiceId('');
        setItemValue('');
        await refreshTable(itemTableId);
    };

    const handleDeleteItem = async (tableId, itemId) => {
        await api.delete(`v1/apps/${appId}/price_tables/${tableId}/items/${itemId}/`);
        await refreshTable(tableId);
    };

    return (
        <Box>
            <PageHeader
                icon={IconTag}
                gradient="amber"
                title="Tabelas de Preços"
                subtitle="Preços específicos por cliente, ou uma tabela padrão geral"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={() => setCreateDialogOpen(true)}>
                        Nova Tabela
                    </Button>
                }
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Nome</TableCell>
                                <TableCell>Cliente</TableCell>
                                <TableCell>Vigência</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tables.map((table) => {
                                const isExpanded = !!expanded[table.id];
                                const detail = tableDetails[table.id];
                                return (
                                    <React.Fragment key={table.id}>
                                        <TableRow hover>
                                            <TableCell padding="checkbox">
                                                <IconButton size="small" onClick={() => toggleExpand(table.id)}>
                                                    {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{table.name}</TableCell>
                                            <TableCell>
                                                {table.organization_name || <Chip label="Padrão (geral)" size="small" variant="outlined" />}
                                            </TableCell>
                                            <TableCell>
                                                {table.valid_from || '...'} - {table.valid_until || '...'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleDeleteTable(table)}>
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                                                        <Table size="small" sx={{ mb: 2 }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Item</TableCell>
                                                                    <TableCell align="right">Preço</TableCell>
                                                                    <TableCell align="right" />
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {(detail?.items || []).map((item) => (
                                                                    <TableRow key={item.id}>
                                                                        <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                                                        <TableCell align="right">{formatCurrencyBR(item.valor)}</TableCell>
                                                                        <TableCell align="right">
                                                                            <IconButton size="small" onClick={() => handleDeleteItem(table.id, item.id)}>
                                                                                <IconTrash size={14} />
                                                                            </IconButton>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                                {detail && detail.items.length === 0 && (
                                                                    <TableRow>
                                                                        <TableCell colSpan={3} align="center">
                                                                            Nenhum item nesta tabela
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>

                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                            <ToggleButtonGroup
                                                                size="small"
                                                                exclusive
                                                                value={itemTableId === table.id ? itemType : null}
                                                                onChange={(e, value) => {
                                                                    if (!value) return;
                                                                    setItemType(value);
                                                                    setItemTableId(table.id);
                                                                }}
                                                            >
                                                                <ToggleButton value="produto">Produto</ToggleButton>
                                                                <ToggleButton value="servico">Serviço</ToggleButton>
                                                            </ToggleButtonGroup>

                                                            {itemTableId === table.id && itemType === 'produto' && (
                                                                <>
                                                                    <TextField
                                                                        select
                                                                        size="small"
                                                                        label="Produto"
                                                                        sx={{ width: 160 }}
                                                                        value={selectedProductId}
                                                                        onChange={(e) => {
                                                                            setSelectedProductId(e.target.value);
                                                                            setSelectedVariacaoId('');
                                                                        }}
                                                                    >
                                                                        {products.map((p) => (
                                                                            <MenuItem key={p.id} value={p.id}>
                                                                                {p.nome}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                    <TextField
                                                                        select
                                                                        size="small"
                                                                        label="Variação"
                                                                        sx={{ width: 140 }}
                                                                        disabled={!selectedProductId}
                                                                        value={selectedVariacaoId}
                                                                        onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                                                    >
                                                                        {variacoesDoProduto.map((v) => (
                                                                            <MenuItem key={v.id} value={v.id}>
                                                                                {v.nome_variacao}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </>
                                                            )}

                                                            {itemTableId === table.id && itemType === 'servico' && (
                                                                <TextField
                                                                    select
                                                                    size="small"
                                                                    label="Serviço"
                                                                    sx={{ width: 160 }}
                                                                    value={selectedServiceId}
                                                                    onChange={(e) => setSelectedServiceId(e.target.value)}
                                                                >
                                                                    {services.map((s) => (
                                                                        <MenuItem key={s.id} value={s.id}>
                                                                            {s.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            )}

                                                            <TextField
                                                                size="small"
                                                                label="Preço"
                                                                type="number"
                                                                sx={{ width: 110 }}
                                                                value={itemTableId === table.id ? itemValue : ''}
                                                                onChange={(e) => {
                                                                    setItemTableId(table.id);
                                                                    setItemValue(e.target.value);
                                                                }}
                                                            />
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    setItemTableId(table.id);
                                                                    handleAddItem();
                                                                }}
                                                            >
                                                                Adicionar
                                                            </Button>
                                                        </Stack>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {tables.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma tabela de preços cadastrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Tabela de Preços</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField label="Nome" fullWidth required value={newTableName} onChange={(e) => setNewTableName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sx={{ position: 'relative' }}>
                            <TextField
                                label="Cliente (opcional - vazio = tabela padrão)"
                                fullWidth
                                value={orgSearch}
                                onChange={handleOrgSearchChange}
                                placeholder="Buscar por nome..."
                            />
                            {orgResults.length > 0 && !selectedOrg && (
                                <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 200, overflowY: 'auto' }}>
                                    <List dense>
                                        {orgResults.map((org) => (
                                            <ListItem key={org.id} disablePadding>
                                                <ListItemButton
                                                    onClick={() => {
                                                        setSelectedOrg(org);
                                                        setOrgSearch(org.name);
                                                        setOrgResults([]);
                                                    }}
                                                >
                                                    {org.name}
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Vigência de (opcional)"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={validFrom}
                                onChange={(e) => setValidFrom(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Vigência até (opcional)"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={validUntil}
                                onChange={(e) => setValidUntil(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCreateTable}>
                        Criar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PriceTables;
